/*
 * Copyright (C) 2026 - present Marek Kuzora - All Rights Reserved.
 */


import Logic from 'fierry/data/logic';

import DestroyEvent from 'solis/logic/board/zone/timeline/destroy';
import ExpiryEvent from 'solis/logic/board/zone/timeline/expiry';
import FadeOutEvent from 'solis/logic/board/zone/timeline/fade.out';
import FlipEvent from 'solis/logic/board/zone/timeline/flip';
import InfluenceEvent from 'solis/logic/board/zone/timeline/influence';
import InitialEvent from 'solis/logic/board/zone/timeline/initial';
import StabilizeEvent from 'solis/logic/board/zone/timeline/stabilize';
import SurrenderEvent from 'solis/logic/board/zone/timeline/surrender';


const CANDIDATES = [ ExpiryEvent, FlipEvent, StabilizeEvent, FadeOutEvent ];


export default class ZoneTimeline extends Logic
{
  static create (zone)
  {
    return new ZoneTimeline(zone, [ InitialEvent.create(zone) ]);
  }


  static createWithInfluence (zone, date, sourceColony, value)
  {
    const initialEvent = InitialEvent.create(zone);

    const influenceEvent = InfluenceEvent.create(zone, date, sourceColony, value);

    return new ZoneTimeline(zone, [ initialEvent, influenceEvent ], [ date, sourceColony, value ]);
  }


  static createWithSurrender (zone, date)
  {
    const initialEvent = InitialEvent.create(zone);

    const surrenderEvent = SurrenderEvent.create(zone, date);

    return new ZoneTimeline(zone, [ initialEvent, surrenderEvent ], [ date ]);
  }


  constructor (zone, initialEvents, dependences = [])
  {
    super(zone, ...dependences);

    this.zone = zone;

    this.futureEvents = [];
    this.initialEvents = initialEvents;

    let event = null;

    while (event = this.findNextEvent())
    {
      if (event.date > this.getEventHorizon())
      {
        this.futureEvents.push(DestroyEvent.create(this.getLastZone()));

        break;
      }

      this.futureEvents.push(event);
    }
  }


  findNextEvent ()
  {
    const zone = this.getLastZone();

    let next = null;

    for (const Candidate of CANDIDATES)
    {
      const candidate = Candidate.create(zone);

      if (candidate.date !== null && (next === null || candidate.date < next.date))
      {
        next = candidate;
      }
    }

    return next;
  }


  getLastZone ()
  {
    return this.getEvents().at(-1).zone;
  }


  getEventHorizon ()
  {
    return this.zone.getBoard().cass.destroyDate;
  }


  getEvents ()
  {
    return [ ...this.initialEvents, ...this.futureEvents ];
  }



  getFutureEvents (date)
  {
    return this.futureEvents.filter(event => event.date > date);
  }


  hasEvent (event)
  {
    return this.futureEvents.some(other => other.equals(event));
  }


  getZone (date)
  {
    return this.getEvent(date).getZone();
  }


  getEvent (date)
  {
    let found = this.initialEvents[0];

    for (const event of this.getEvents())
    {
      if (event.date > date)
      {
        break;
      }

      found = event;
    }

    return found;
  }


  getFlipDate ()
  {
    const event = this.futureEvents.find(event => event instanceof FlipEvent);

    return event ? event.date : null;
  }


  getControl (user, date)
  {
    return this.getEvent(date).getControl(user, date);
  }


  getControlRate (user, date)
  {
    return this.getEvent(date).getControlRate(user, date);
  }


  getControlWinner (date)
  {
    return this.getEvent(date).getControlWinner(date);
  }


  getInfluence (user, date)
  {
    return this.getEvent(date).getInfluence(user);
  }


  getWinner (date)
  {
    return this.getEvent(date).getWinner();
  }


  getActiveUsers (date)
  {
    return this.getEvent(date).getActiveUsers();
  }


  getControlUsers (date)
  {
    return this.getEvent(date).getControlUsers(date);
  }


  isContested (date)
  {
    return this.getEvent(date).isContested();
  }


};
